import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Calendar,
  CheckCircle,
  ChevronDown,
  Clock,
  DollarSign,
  FileText,
  Repeat,
  X,
} from 'lucide-react';
import { CategorySelector } from './CategorySelector';
import {
  useFinanceStore,
  Transaction,
  TransactionRecurrence,
} from '../store/useFinanceStore';
import { toDateInputValue } from '../utils/date';
import { useTheme } from '../contexts/ThemeContext';

interface NewTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FormState = {
  description: string;
  amount: string;
  category: Transaction['category'];
  type: 'income' | 'expense';
  date: string;
  category_id: string | null;
  categoryName: string;
  notes: string;
  recurrenceType: TransactionRecurrence;
  recurrenceEndDate: string;
  recurrenceCount: string;
  customCategoryName: string;
  customSubcategoryName: string;
  isForecast: boolean;
};

type ThemeVariant = 'light' | 'dark';

type RecurrenceOption = {
  value: TransactionRecurrence;
  label: string;
  helper: string;
};

const LEGACY_CATEGORIES: Transaction['category'][] = [
  'Moradia',
  'Mercado',
  'Outros',
  'Entrada principal',
  'Financeiro',
  'Freelance',
  'Aporte da Meta',
];

const recurrenceOptions: RecurrenceOption[] = [
  { value: 'none', label: 'Sem repetição', helper: 'Lançamento único' },
  { value: 'weekly', label: 'Semanal', helper: 'Repete a cada semana' },
  { value: 'biweekly', label: 'Quinzenal', helper: 'Repete a cada 15 dias' },
  { value: 'monthly', label: 'Mensal', helper: 'Repete no mesmo dia do mês' },
  { value: 'annual', label: 'Anual', helper: 'Repete todo ano' },
];

const themeTokens = {
  light: {
    backdrop: 'bg-black/40',
    container: 'bg-white text-slate-900 border border-teal-100 shadow-2xl',
    containerText: 'text-slate-900',
    headerAccentBg: 'bg-emerald-100',
    headerAccentIcon: 'text-emerald-600',
    label: 'text-slate-600',
    helper: 'text-slate-500',
    helperBody: 'text-slate-500',
    helperMuted: 'text-slate-400',
    accentLabel: 'text-emerald-600',
    input: 'bg-white border border-teal-200 placeholder:text-slate-400 focus:border-teal-500 focus:ring-teal-500/20 text-slate-800',
    inactiveToggle: 'bg-teal-50 text-teal-600 border border-teal-100 hover:bg-teal-100',
    sectionCard: 'bg-white/80 border border-teal-100',
    recurrenceInactive: 'border-teal-100 bg-white text-slate-600 hover:border-teal-200',
    cancelButton: 'bg-white text-slate-700 border border-teal-100 hover:bg-teal-50',
    mutedIcon: 'text-teal-500',
  },
  dark: {
    backdrop: 'bg-black/60',
    container: 'bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white border border-slate-700/60',
    containerText: 'text-white',
    headerAccentBg: 'bg-emerald-500/20',
    headerAccentIcon: 'text-emerald-300',
    label: 'text-slate-300',
    helper: 'text-slate-400',
    helperBody: 'text-slate-400',
    helperMuted: 'text-slate-500',
    accentLabel: 'text-emerald-300',
    input: 'bg-slate-900/40 border border-slate-700 placeholder:text-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20 text-slate-100',
    inactiveToggle: 'bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-750',
    sectionCard: 'bg-slate-900/60 border border-slate-700/60',
    recurrenceInactive: 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600',
    cancelButton: 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700',
    mutedIcon: 'text-slate-400',
  },
} as const satisfies Record<ThemeVariant, Record<string, string>>;

const createInitialFormState = (): FormState => ({
  description: '',
  amount: '',
  category: 'Outros',
  type: 'expense',
  date: toDateInputValue(),
  category_id: null,
  categoryName: '',
  notes: '',
  recurrenceType: 'none',
  recurrenceEndDate: '',
  recurrenceCount: '',
  customCategoryName: '',
  customSubcategoryName: '',
  isForecast: false,
});

const mapCategoryNameToLegacy = (name: string | null): Transaction['category'] => {
  if (!name) return 'Outros';
  const match = LEGACY_CATEGORIES.find((category) => category.toLowerCase() === name.toLowerCase());
  return match ?? 'Outros';
};

const NewTransactionModal: React.FC<NewTransactionModalProps> = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const mode: ThemeVariant = theme === 'dark' ? 'dark' : 'light';
  const tokens = themeTokens[mode];
  const addTransaction = useFinanceStore((state) => state.addTransaction);
  const categoriesWithSubs = useFinanceStore((state) => state.categoriesWithSubs);
  const [formData, setFormData] = useState<FormState>(() => createInitialFormState());
  const [useCustomCategory, setUseCustomCategory] = useState(false);
  const [isRecurrenceOpen, setIsRecurrenceOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(createInitialFormState());
      setUseCustomCategory(false);
      setIsRecurrenceOpen(false);
    }
  }, [isOpen]);

  const flattenedCategories = useMemo(() => {
    return categoriesWithSubs.flatMap((category) => [category, ...category.subcategories]);
  }, [categoriesWithSubs]);

  const selectedCategory = useMemo(() => {
    return flattenedCategories.find((category) => category.id === formData.category_id) ?? null;
  }, [flattenedCategories, formData.category_id]);

  const isOutrosCategory = useMemo(() => {
    if (useCustomCategory) return false;
    if (selectedCategory) {
      return selectedCategory.name.toLowerCase() === 'outros';
    }
    return formData.category === 'Outros';
  }, [selectedCategory, useCustomCategory, formData.category]);

  const handleCategoryChange = (categoryId: string, categoryName: string) => {
    setFormData((prev) => ({
      ...prev,
      category_id: categoryId,
      categoryName,
      category: mapCategoryNameToLegacy(categoryName),
    }));
    setUseCustomCategory(false);
  };

  const handleToggleCustomCategory = () => {
    setUseCustomCategory((prev) => {
      const next = !prev;
      if (next) {
        setFormData((current) => ({
          ...current,
          category_id: null,
          categoryName: '',
          category: 'Outros',
        }));
      }
      return next;
    });
  };

  const handleClose = () => {
    setFormData(createInitialFormState());
    setUseCustomCategory(false);
    setIsRecurrenceOpen(false);
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formData.description.trim()) {
      alert('Informe a descrição da transação.');
      return;
    }

    const amountValue = Number.parseFloat(formData.amount.replace(',', '.'));
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      alert('Informe um valor válido.');
      return;
    }

    if (isOutrosCategory && !formData.notes.trim()) {
      alert('Adicione uma observação para a categoria "Outros".');
      return;
    }

    if (useCustomCategory && !formData.customCategoryName.trim()) {
      alert('Informe o nome da categoria personalizada.');
      return;
    }

    const recurrenceCount = formData.recurrenceCount ? Number.parseInt(formData.recurrenceCount, 10) : null;

    try {
      await addTransaction({
        description: formData.description.trim(),
        amount: formData.type === 'expense' ? -Math.abs(amountValue) : Math.abs(amountValue),
        category: formData.category,
        type: formData.type,
        date: formData.date,
        category_id: useCustomCategory ? null : formData.category_id,
        notes: formData.notes.trim() ? formData.notes.trim() : null,
        customCategoryName: useCustomCategory ? formData.customCategoryName.trim() : null,
        customSubcategoryName: useCustomCategory ? (formData.customSubcategoryName.trim() || null) : null,
        recurrenceType: formData.recurrenceType,
        recurrenceCount,
        recurrenceEndDate: formData.recurrenceEndDate || null,
        status: formData.isForecast ? 'scheduled' : 'confirmed',
        isForecast: formData.isForecast,
      });

      handleClose();
    } catch (error) {
      console.error('Falha ao salvar transação', error);
    }
  };

  const inputClass = `w-full px-4 py-3 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${tokens.input}`;
  const inactiveToggleClass = `px-4 py-3 rounded-lg font-medium transition-all duration-200 ${tokens.inactiveToggle}`;
  const recurrenceOptionInactiveClass = `${tokens.recurrenceInactive} px-4 py-3 rounded-lg transition-all duration-200`;
  const sectionCardClass = `rounded-xl overflow-hidden ${tokens.sectionCard}`;
  const helperTextClass = tokens.helper;
  const helperBodyClass = tokens.helperBody;
  const mutedIconClass = tokens.mutedIcon;
  const cancelButtonClass = `flex-1 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${tokens.cancelButton}`;
  const containerTextClass = tokens.containerText;
  const accentLabelClass = tokens.accentLabel;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className={`absolute inset-0 backdrop-blur-sm ${tokens.backdrop}`}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
            className={`relative w-full max-w-lg mx-4 max-h-[90vh] flex flex-col overflow-hidden rounded-2xl border transition-colors duration-300 ${tokens.container}`}
          >
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${tokens.headerAccentBg}`}>
                  <DollarSign className={tokens.headerAccentIcon} size={20} />
                </div>
                <h2 className={`text-xl font-semibold ${containerTextClass}`}>Nova Transação</h2>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-lg transition-colors duration-200 hover:bg-black/10 text-inherit"
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto px-6 pb-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>Tipo</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, type: 'income' }))}
                      className={
                        formData.type === 'income'
                          ? 'px-4 py-3 rounded-lg font-medium transition-all duration-200 bg-emerald-600 text-white shadow-lg shadow-emerald-900/30'
                          : inactiveToggleClass
                      }
                    >
                      Receita
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, type: 'expense' }))}
                      className={
                        formData.type === 'expense'
                          ? 'px-4 py-3 rounded-lg font-medium transition-all duration-200 bg-red-600 text-white shadow-lg shadow-red-900/30'
                          : inactiveToggleClass
                      }
                    >
                      Despesa
                    </button>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>
                    Confirmação do lançamento
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, isForecast: false }))}
                      className={
                        !formData.isForecast
                          ? 'px-4 py-3 rounded-lg font-medium transition-all duration-200 bg-emerald-600 text-white shadow-lg shadow-emerald-900/30 flex items-center justify-center gap-2'
                          : `${inactiveToggleClass} flex items-center justify-center gap-2`
                      }
                    >
                      <CheckCircle size={18} />
                      Confirmado
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, isForecast: true }))}
                      className={
                        formData.isForecast
                          ? 'px-4 py-3 rounded-lg font-medium transition-all duration-200 bg-amber-600 text-white shadow-lg shadow-amber-900/30 flex items-center justify-center gap-2'
                          : `${inactiveToggleClass} flex items-center justify-center gap-2`
                      }
                    >
                      <Clock size={18} />
                      Previsto
                    </button>
                  </div>
                  <p className={`text-xs mt-2 ${helperTextClass}`}>
                    Lançamentos previstos entram nas projeções futuras até a confirmação do pagamento.
                  </p>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>Descrição</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
                    placeholder="Ex: Compra no supermercado"
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>Valor</label>
                  <div className="relative">
                    <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${mutedIconClass}`}>R$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.amount}
                      onChange={(event) => setFormData((prev) => ({ ...prev, amount: event.target.value }))}
                      placeholder="0,00"
                      className={`${inputClass} pl-10`}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  {!useCustomCategory ? (
                    <>
                      <CategorySelector
                        value={formData.category_id}
                        onChange={handleCategoryChange}
                        type={formData.type}
                        appearance={mode}
                      />
                      <button
                        type="button"
                        onClick={handleToggleCustomCategory}
                        className="text-sm font-medium transition-colors duration-200 text-emerald-500 hover:text-emerald-400"
                      >
                        Cadastrar categoria personalizada
                      </button>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>
                          Nome da categoria
                        </label>
                        <input
                          type="text"
                          value={formData.customCategoryName}
                          onChange={(event) =>
                            setFormData((prev) => ({ ...prev, customCategoryName: event.target.value }))
                          }
                          placeholder="Ex: Terapias alternativas"
                          className={inputClass}
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>
                          Nome da subcategoria <span className={helperTextClass}>(opcional)</span>
                        </label>
                        <input
                          type="text"
                          value={formData.customSubcategoryName}
                          onChange={(event) =>
                            setFormData((prev) => ({ ...prev, customSubcategoryName: event.target.value }))
                          }
                          placeholder="Ex: Consulta mensal"
                          className={inputClass}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleToggleCustomCategory}
                        className="text-sm font-medium transition-colors duration-200 text-slate-400 hover:text-slate-200"
                      >
                        Voltar para lista de categorias
                      </button>
                    </div>
                  )}

                  <div className={sectionCardClass}>
                    <button
                      type="button"
                      onClick={() => setIsRecurrenceOpen((prev) => !prev)}
                      className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left transition-colors duration-200 hover:bg-black/5"
                    >
                      <div>
                        <span className={`text-xs font-semibold tracking-[0.14em] uppercase block ${accentLabelClass}`}>
                          Recorrência
                        </span>
                        <span className={`text-sm ${helperBodyClass}`}>
                          Organize pagamentos repetidos com regras claras
                        </span>
                      </div>
                      <ChevronDown
                        className={`transition-transform duration-200 ${
                          isRecurrenceOpen ? 'rotate-180' : ''
                        } ${mutedIconClass}`}
                        size={20}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {isRecurrenceOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="border-t border-white/10"
                        >
                          <div className="p-4 space-y-4">
                            <div>
                              <span className={`text-xs font-semibold tracking-[0.14em] uppercase block ${accentLabelClass}`}>
                                Frequência
                              </span>
                              <span className={`text-sm ${helperBodyClass}`}>
                                Escolha com que frequência este lançamento deve se repetir
                              </span>
                            </div>

                            <div className="grid gap-2">
                              {recurrenceOptions.map((option) => {
                                const isActive = formData.recurrenceType === option.value;
                                return (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() =>
                                      setFormData((prev) => ({ ...prev, recurrenceType: option.value }))
                                    }
                                    className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border text-left transition-all duration-200 ${
                                      isActive
                                        ? 'bg-emerald-500/10 border-emerald-500 text-emerald-300 shadow shadow-emerald-500/10'
                                        : recurrenceOptionInactiveClass
                                    }`}
                                  >
                                    <div>
                                      <span className="font-medium block">{option.label}</span>
                                      <span className={`text-sm ${helperBodyClass}`}>{option.helper}</span>
                                    </div>
                                    {isActive && <CheckCircle size={18} className="text-emerald-400" />}
                                  </button>
                                );
                              })}
                            </div>

                            {formData.recurrenceType !== 'none' && (
                              <div className="grid gap-3">
                                <div>
                                  <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>
                                    Data final da recorrência
                                  </label>
                                  <div className="relative">
                                    <input
                                      type="date"
                                      value={formData.recurrenceEndDate}
                                      onChange={(event) =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          recurrenceEndDate: event.target.value,
                                        }))
                                      }
                                      className={`${inputClass} pr-10`}
                                      min={formData.date}
                                    />
                                    <Calendar size={18} className={`absolute right-3 top-1/2 -translate-y-1/2 ${mutedIconClass}`} />
                                  </div>
                                  <p className={`text-xs mt-2 ${helperTextClass}`}>
                                    Deixe em branco se quiser repetir indefinidamente.
                                  </p>
                                </div>

                                <div>
                                  <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>
                                    Número de repetições
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={formData.recurrenceCount}
                                    onChange={(event) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        recurrenceCount: event.target.value,
                                      }))
                                    }
                                    placeholder="Ex: 12 (sugestão para um ano)"
                                    className={inputClass}
                                  />
                                  <p className={`text-xs mt-2 ${helperTextClass}`}>
                                    Preencha apenas se quiser limitar a quantidade de repetições.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>Data</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(event) => setFormData((prev) => ({ ...prev, date: event.target.value }))}
                      className={`${inputClass} pr-10`}
                      required
                    />
                    <Calendar size={18} className={`absolute right-3 top-1/2 -translate-y-1/2 ${mutedIconClass}`} />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>
                    Observações{' '}
                    <span className={isOutrosCategory ? 'text-red-500' : helperTextClass}>
                      {isOutrosCategory ? '(obrigatório para "Outros")' : '(opcional)'}
                    </span>
                  </label>
                  <div className="relative">
                    <FileText size={18} className={`absolute left-3 top-3 ${mutedIconClass}`} />
                    <textarea
                      value={formData.notes}
                      onChange={(event) => setFormData((prev) => ({ ...prev, notes: event.target.value }))}
                      placeholder={
                        isOutrosCategory
                          ? 'Descreva do que se trata esta transação'
                          : 'Adicione detalhes relevantes sobre a transação'
                      }
                      rows={3}
                      className={`${inputClass} pl-10 resize-none`}
                      required={isOutrosCategory}
                    />
                  </div>
                </div>

                {formData.recurrenceType !== 'none' && (
                  <div className={sectionCardClass}>
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
                      <Repeat size={20} className={mutedIconClass} />
                      <div>
                        <span className={`text-xs font-semibold tracking-[0.14em] uppercase block ${accentLabelClass}`}>
                          Preview da recorrência
                        </span>
                        <span className={`text-sm ${helperBodyClass}`}>
                          Veja um resumo das próximas ocorrências deste lançamento
                        </span>
                      </div>
                    </div>

                    <div className="p-4 space-y-2 text-sm">
                      <div className="flex items-start justify-between">
                        <span className={helperBodyClass}>Tipo de recorrência</span>
                        <span className="font-medium">
                          {
                            recurrenceOptions.find((option) => option.value === formData.recurrenceType)?.label ??
                              'Sem repetição'
                          }
                        </span>
                      </div>
                      {formData.recurrenceEndDate && (
                        <div className="flex items-start justify-between">
                          <span className={helperBodyClass}>Data final</span>
                          <span className="font-medium">{formData.recurrenceEndDate}</span>
                        </div>
                      )}
                      {formData.recurrenceCount && (
                        <div className="flex items-start justify-between">
                          <span className={helperBodyClass}>Número de repetições</span>
                          <span className="font-medium">{formData.recurrenceCount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={handleClose} className={cancelButtonClass}>
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 rounded-lg font-semibold transition-all duration-200 bg-emerald-600 text-white shadow-lg shadow-emerald-900/30 hover:bg-emerald-500"
                  >
                    Salvar transação
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default NewTransactionModal;